<template>
  <section class="i-coaches-section">
    <div class="container-wide">
      <div
        class="container"
        style="padding: 0"
      >
        <div class="label mb-3">Who you're talking to</div>
        <h2 class="headline">Former recruiters.<br />Not career generalists.</h2>
        <p
          class="body-lg"
          style="color: var(--text-body); margin-top: 12px; max-width: 560px; margin-bottom: 36px"
        >
          Every coach on our platform spent years on the other side of the table — deciding who gets interviewed, who
          gets offers, and who gets passed over. They know what your file looks like from the inside.
        </p>
      </div>

      <div class="grid-3">
        <div
          v-for="coach in coaches"
          :key="coach.name"
          class="coach-card"
        >
          <div class="coach-card__header">
            <div class="coach-card__avatar">
              <img
                :class="{ zoom: coach.image === Zanetta }"
                :src="coach.image"
              />
            </div>
            <div>
              <div class="coach-card__name">{{ coach.name }}</div>
              <div
                class="coach-card__title"
                v-html="coach.title"
              ></div>
            </div>
          </div>
          <div class="coach-card__stats">
            <div
              v-for="stat in coach.stats"
              :key="stat.label"
              class="coach-card__stat"
            >
              <span class="coach-card__stat-num">{{ stat.num }}</span>
              <span class="coach-card__stat-label">{{ stat.label }}</span>
            </div>
          </div>
          <div class="coach-card__quote">"{{ coach.quote }}"</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"

// ASSETS
import Julie from "@/assets/images/coach-julie.png"
import Zanetta from "@/assets/images/coach-zanetta.jpg"
import Linda from "@/assets/images/coach-linda.webp"

// PROPS
const props = defineProps({
  timing: {
    type: String,
    default: "30"
  }
})

// COMPUTED
const coaches = computed(() => [
  {
    image: Julie,
    avatarShadow: "0 4px 16px rgba(245,158,11,0.25)",
    name: "Julie MacWilliams",
    title: "Former Director of HR &amp; Talent Strategy<br>15+ years across tech, finance, retail &amp; media",
    stats: [
      { num: "10,000+", label: "hiring decisions" },
      { num: "15 yrs", label: "talent strategy" }
    ],
    quote: `I've sat on the other side of every hiring decision — I know exactly why great candidates get passed over. It's almost never about qualifications. It's about positioning. And positioning is fixable in ${props.timing} minutes.`
  },
  {
    image: Linda,
    avatarShadow: "0 4px 16px rgba(91,76,255,0.3)",
    name: "Linda Lutz",
    title: "Head of Coaching, JobTest<br>22+ years in career strategy",
    stats: [
      { num: "1,000s", label: "career changes guided" },
      { num: "22 yrs", label: "coaching experience" }
    ],
    quote:
      "Most people aren't stuck because they lack talent — they're stuck because no one's shown them what their strengths actually qualify them for. That's what the first 10 minutes of every session reveals."
  },
  {
    image: Zanetta,
    avatarShadow: "0 4px 16px rgba(16,185,129,0.25)",
    name: "Zanetta Siciliano",
    title: "Executive Career Coach &amp; Leadership Advisor<br>Former executive recruiter · 19+ years",
    stats: [
      { num: "30,000+", label: "professionals coached" },
      { num: "19 yrs", label: "executive coaching" }
    ],
    quote:
      "I've coached 30,000 professionals across 92 countries. The pattern is always the same — people undervalue what they bring to the table and oversell the wrong things. One conversation changes that."
  }
])
</script>

<style lang="scss" scoped>
.i-coaches-section {
  .coach-card {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: var(--card-shadow);
    transition:
      box-shadow 0.25s,
      transform 0.25s;

    &:hover {
      box-shadow: var(--card-shadow-lg);
      transform: translateY(-3px);
    }

    &__header {
      display: flex;
      align-items: start;
      gap: 14px;
    }

    &__avatar {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      font-weight: 800;
      color: #fff;
      flex-shrink: 0;
      overflow: hidden;

      .zoom {
        transform: scale(1.3);
        object-fit: cover;
      }
    }

    &__name {
      font-weight: 700;
      font-size: 1rem;
      color: var(--text);
    }

    &__title {
      font-size: 0.78rem;
      color: var(--muted);
      line-height: 1.4;
      margin-top: 1px;
    }

    &__stats {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      padding-top: 4px;
    }

    &__stat {
      font-size: 0.8rem;
    }

    &__stat-num {
      font-size: 1.1rem;
      font-weight: 800;
      color: var(--text);
      display: block;
      line-height: 1;
      margin-bottom: 2px;
    }

    &__stat-label {
      color: var(--muted);
    }

    &__quote {
      font-style: italic;
      font-size: 0.85rem;
      color: var(--text-body);
      border-top: 1px solid var(--border);
      padding-top: 16px;
      line-height: 1.6;
    }
  }
}
</style>
